import { useCallback, useEffect, useState } from 'react';
import { facilityApi } from '../../api/api';
import { notify } from '../../utils/notifications';

const PencilIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);

export default function FacilityNameEditor({ facilityId }) {
    const [displayName, setDisplayName] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const fac = await facilityApi.get(facilityId);
            setDisplayName(fac.name || '');
        } catch {
            notify.error('Failed to load facility');
        }
    }, [facilityId]);

    useEffect(() => { load(); }, [load]);

    const startEdit = () => {
        setEditValue(displayName ?? '');
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setEditValue('');
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') cancelEdit();
        if (e.key === 'Enter' && !saving && editValue.trim()) onSave();
    };

    const onSave = async () => {
        const trimmed = editValue.trim();
        if (!trimmed) return;
        setSaving(true);
        try {
            await facilityApi.update(facilityId, { name: trimmed });
            notify.success('Saved');
            setDisplayName(trimmed);
            setEditing(false);
            setEditValue('');
        } catch (err) {
            notify.error(err?.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <span className="facility-name-editor facility-name-editor--editing">
                <input
                    type="text"
                    aria-label="Facility name"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    autoFocus
                />
                <button
                    className="facility-name-editor__save"
                    onClick={onSave}
                    disabled={saving || !editValue.trim()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
                <button className="facility-name-editor__cancel" onClick={cancelEdit}>Cancel</button>
            </span>
        );
    }

    return (
        <span className="facility-name-editor">
            <span>{displayName !== null ? displayName : facilityId}</span>
            <button
                aria-label="Edit facility name"
                onClick={startEdit}
                className="facility-name-editor__edit-btn"
            >
                <PencilIcon />
            </button>
        </span>
    );
}
